import { DEFAULT_CHAT_LANGUAGE, useApp, useAppSelector, useUserInteractions, useUserSettings } from "@next-platform/client-common-core";
import { SubCapabilityKey, useApplicationCapabilitiesAllowed } from "@next-platform/client-common-core/infra/application/capabilities";
import basicAppConfig from "@next-platform/client-common-core/infra/constants/basicAppConfig";
import createUncachedLorebookSearchConfig from "@next-platform/client-common-core/lorebook/createUncachedLorebookSearchConfig";
import { FetchStatus } from "@next-platform/client-common-core/user/userSettings/types";
import { Button, ButtonVariant, IconVariants, TabItem, Tabs, Text, TextVariants } from "@next-platform/client-common-spicy-ui";
import { CharacterType } from "@next-platform/packages-ts-types";
import { Spacer } from "@nextui-org/react";
import { UiState } from "instantsearch.js";
import { history } from "instantsearch.js/es/lib/routers";
import React, { useEffect, useMemo, useState } from "react";
import { Configure, ConfigureProps, InstantSearch } from "react-instantsearch";
import { useParams } from "react-router-dom";
import InstantSearchAdapter, { TypesenseInstantsearchAdapterOptions } from "typesense-instantsearch-adapter";

import BannedBanner from "@/components/BannedBanner";
import { Col, Row, Spinner } from "@/components/customComponents";
import ErrorSection from "@/components/ErrorSection/ErrorSection";
import FilterBox from "@/components/search/FilterBox/FilterBox";
import { SearchAdapter } from "@/components/search/types";
import Trans from "@/components/Trans/Trans";
import {
  DEFAULT_INCLUDE_FIELDS_FOR_SEARCH_CLIENT,
  DEFAULT_INCLUDE_FIELDS_FOR_SEARCH_CLIENT_LOREBOOKS,
  DEFAULT_INCLUDE_FIELDS_FOR_SEARCH_CLIENT_RULEBOOKS,
  DEFAULT_QUERY_BY_FOR_SEARCH_CLIENT,
  DEFAULT_QUERY_BY_FOR_SEARCH_CLIENT_LOREBOOKS,
  DEFAULT_QUERY_BY_FOR_SEARCH_CLIENT_RULEBOOKS,
  NSFW_TOGGLE_LOCALSTORAGE_KEY,
  TAGS_FILTER_LOCAL_STORAGE_KEY,
  TYPESENSE_TEXT_MATCH_SORT_PREFIX,
} from "@/constants";
import useLocalizedLocation from "@/hooks/useLocalizedLocation";
import useLocalizedNavigation from "@/hooks/useLocalizedNavigation";
import useAuth from "@/services/useAuth";
import { Collection, TypesenseApiKey, TypesenseCollectionName } from "@/types/api";
import { cn } from "@/utils";
import { getIsPartner } from "@/utils/getIsPartner";
import { typedLocalStorageService } from "@/utils/localStorageRe";

import FiltersV2Provider from "./FiltersV2/FiltersV2Provider";
import HeroBanner from "./HeroBanner";
import CharacterHits from "./Hits";
import useShouldFilterNsfwCharacters from "./hooks/useShouldFilterNsfwCharacters";
import NsfwToggle from "./NsfwToggle";
import SearchBar from "./SearchBar";
import SearchBySort from "./SearchBySort";
import SearchClientPagination from "./SearchClientPagination";
import SearchFilterTriggerSelector from "./SearchFilterTriggerSelector";
import SearchResultsSummary from "./SearchResultsSummary";
import createFilters from "./utils/createFilters";
import { createSearchClientConfig } from "./utils/createSearchClientConfig";
import { parseInstantSearchUrlState } from "./utils/parseInstantSearchUrlState";
import WaitingQueueHeader from "./WaitingQueuHeader";

interface ISearchClientCharacterListingProps {
  charactersList?: string[];
  recommId?: string;
  useStoreTagFilters?: boolean;
  onRefresh?: () => Promise<void>;
  onNsfwToggle?: React.Dispatch<React.SetStateAction<boolean>>;
  refetchingRecommendations?: boolean;
  defaultTag?: string;
  defaultSortField?: string;
  collection?: Collection;
  tabItems?: TabItem[];
  activeTab?: string;
  onTabChange?: (tabName: string) => void;
  characterType?: CharacterType;
}

/** Collections that share the lorebook-style document shape: no name_i18n fields and no num_messages_24h sort. */
const isNonCharacterCollection = (collection: Collection): boolean => collection === Collection.LOREBOOKS || collection === Collection.RULEBOOKS;

const defaultQueryByFor = (collection: Collection): string => {
  if (collection === Collection.RULEBOOKS) {
    return DEFAULT_QUERY_BY_FOR_SEARCH_CLIENT_RULEBOOKS;
  }
  if (collection === Collection.LOREBOOKS) {
    return DEFAULT_QUERY_BY_FOR_SEARCH_CLIENT_LOREBOOKS;
  }
  return DEFAULT_QUERY_BY_FOR_SEARCH_CLIENT;
};

const defaultIncludeFieldsFor = (collection: Collection): string => {
  if (collection === Collection.RULEBOOKS) {
    return DEFAULT_INCLUDE_FIELDS_FOR_SEARCH_CLIENT_RULEBOOKS;
  }
  if (collection === Collection.LOREBOOKS) {
    return DEFAULT_INCLUDE_FIELDS_FOR_SEARCH_CLIENT_LOREBOOKS;
  }
  return DEFAULT_INCLUDE_FIELDS_FOR_SEARCH_CLIENT;
};

const SearchClientCharacterListing = ({
  charactersList = [],
  recommId = "",
  useStoreTagFilters = true,
  onRefresh,
  onNsfwToggle,
  refetchingRecommendations = false,
  defaultTag,
  defaultSortField,
  collection = Collection.CHARACTERS,
  tabItems,
  activeTab,
  onTabChange,
  characterType,
}: ISearchClientCharacterListingProps) => {
  const isFiltersV2Allowed = useApplicationCapabilitiesAllowed(SubCapabilityKey.DiscoverFiltersV2);
  const isFiltersV2 = isFiltersV2Allowed && collection === Collection.CHARACTERS;
  const { typesenseConfig: searchClientRemoteConfig } = useApp();
  const applicationId = basicAppConfig.applicationId;
  const { setUserSortBy } = useUserInteractions();

  const { showNSFW, blockedUsers, areUserSettingsLoaded, areUserSettingLoading, chatLanguage } = useUserSettings();
  const [stateInstantSearchAdapter, setStateInstantSearchAdapter] = useState<SearchAdapter | null>(null);
  const [stateDefaultQueryBy, setStateDefaultQueryBy] = useState(defaultQueryByFor(collection));
  const [stateQueryBy, setStateQueryBy] = useState(stateDefaultQueryBy);
  const [stateIncludeFields, setStateIncludeFields] = useState(defaultIncludeFieldsFor(collection));
  const { username } = useParams();
  const nsfwToggleStorage = typedLocalStorageService.getItem(NSFW_TOGGLE_LOCALSTORAGE_KEY);
  const [stateIsNsfwToggled, setStateIsNsfwToggled] = useState(nsfwToggleStorage === "true");
  const { isAuthenticated, permissions } = useAuth();
  const [stateIsFoundNegativeKeyword, setStateIsFoundNegativeKeyword] = useState(false);
  const [stateCurrentCategory, setStateCurrentCategory] = useState("");
  const [stateIsCharactersLoading, setStateIsCharactersLoading] = useState(false);
  const baseIndexName = searchClientRemoteConfig?.[TypesenseCollectionName[collection]] ?? "";
  const apiKey = searchClientRemoteConfig?.[TypesenseApiKey[collection]];
  // Defaulted per collection: num_messages_24h does not exist on lorebooks, so a caller omitting
  // the prop would otherwise sort them by a field they do not have.
  const effectiveSortField = defaultSortField ?? (isNonCharacterCollection(collection) ? "createdAt:desc" : "num_messages_24h:desc");
  const searchIndexName = `${baseIndexName}/sort/${TYPESENSE_TEXT_MATCH_SORT_PREFIX},${effectiveSortField}`;
  const isPartner = getIsPartner();
  const {
    isEffectiveNsfwToggleEnabled,
    isNsfwCharacterDiscoveryAllowed,
    isNsfwToggleAllowed: isNsfwUserToggleAllowed,
    shouldFilterNsfwCharacters,
  } = useShouldFilterNsfwCharacters({
    isNsfwToggled: stateIsNsfwToggled,
    isShowNsfwEnabled: showNSFW,
  });
  const { pagePath } = useLocalizedLocation();
  const localizedNavigate = useLocalizedNavigation();
  const [stateIsTagsDrawerVisible, setStateIsTagsDrawerVisible] = useState(false); // use to controle tags side drawer
  const tagListMinimizedLocalStorage = typedLocalStorageService.getItem(TAGS_FILTER_LOCAL_STORAGE_KEY);
  const [stateIsTagListMinimized, setStateIsTagListMinimized] = useState(tagListMinimizedLocalStorage === "true"); // used to minimize tag list in web
  const { onboardingFinished, showOnboardingModal } = useAppSelector((state) => state.navigation) || {};
  const shouldDelayTypesenseQuery = isAuthenticated && !areUserSettingsLoaded && !onboardingFinished && !showOnboardingModal;
  const isHideFiltersBox = !!defaultTag;

  useEffect(() => {
    if (nsfwToggleStorage === "true") {
      setStateIsNsfwToggled(true);
    }
  }, [nsfwToggleStorage]);

  const onSearchUiStateChange = ({
    uiState,
    setUiState,
  }: {
    uiState: UiState;
    setUiState: (uiState: UiState | ((previousUiState: UiState) => UiState)) => void;
  }) => {
    let sortBy = null;
    if (uiState && Object.hasOwn(uiState, searchIndexName)) {
      const indexName = uiState[searchIndexName];

      if (indexName && Object.hasOwn(indexName, "sortBy")) {
        sortBy = indexName["sortBy"];
      }
    }
    setUserSortBy(sortBy || null);
    setUiState(uiState);
  };

  const nsfwToggleHandler = (isNsfwToggled: boolean) => {
    setStateIsNsfwToggled(isNsfwToggled);
    onNsfwToggle?.(isNsfwToggled);
  };

  const routing = useMemo(
    () => ({
      router: history({
        parseURL({ qsModule, location }) {
          return parseInstantSearchUrlState(qsModule, location?.search ?? "");
        },
        push(url) {
          const urlObject = new URL(url);
          const params = new URLSearchParams(urlObject.search);
          //We exclude nsfw from routing
          params.delete("characters[toggle][is_nsfw]");
          const pathWithQuery = urlObject.pathname + (params.toString() ? `?${params.toString()}` : "");
          localizedNavigate(pathWithQuery, { replace: true });
        },
        getLocation() {
          return window.location;
        },
        cleanUrlOnDispose: false,
      }),
    }),
    [localizedNavigate],
  );

  const searchClientConfig = createSearchClientConfig({
    apiKey,
    queryBy: stateQueryBy,
    includeFields: stateIncludeFields,
  });

  useEffect(() => {
    //We wait for key and chat language to be available before creating the adapter
    if (apiKey && !shouldDelayTypesenseQuery) {
      const configuration = collection === Collection.LOREBOOKS ? createUncachedLorebookSearchConfig(searchClientConfig) : searchClientConfig;
      const instantSearchAdapter = new InstantSearchAdapter(configuration as TypesenseInstantsearchAdapterOptions) as SearchAdapter;
      setStateInstantSearchAdapter(instantSearchAdapter);
    }
  }, [apiKey, shouldDelayTypesenseQuery, collection]);

  useEffect(() => {
    if (stateInstantSearchAdapter) {
      stateInstantSearchAdapter.configuration.additionalSearchParameters.query_by = stateQueryBy;
      stateInstantSearchAdapter.configuration.additionalSearchParameters.include_fields = stateIncludeFields;
    }
  }, [stateQueryBy, stateIncludeFields]);

  useEffect(() => {
    if (chatLanguage && chatLanguage !== DEFAULT_CHAT_LANGUAGE) {
      // The lorebooks collection has no name_i18n field in its Typesense schema, so appending it 404s the query.
      const i18nFields = `,title_i18n.${chatLanguage},name_i18n.${chatLanguage}`;
      const queryByValue = isNonCharacterCollection(collection) ? defaultQueryByFor(collection) : DEFAULT_QUERY_BY_FOR_SEARCH_CLIENT + i18nFields;
      setStateDefaultQueryBy(queryByValue);
      setStateQueryBy(queryByValue);
      const includeFieldsValue = isNonCharacterCollection(collection)
        ? defaultIncludeFieldsFor(collection)
        : DEFAULT_INCLUDE_FIELDS_FOR_SEARCH_CLIENT + i18nFields;
      setStateIncludeFields(includeFieldsValue);
    }
  }, [chatLanguage]);

  if (!applicationId || !apiKey || !stateInstantSearchAdapter?.searchClient) {
    return <Spinner centerToScreen={true} />;
  }

  if (areUserSettingLoading === FetchStatus.failed) {
    return <ErrorSection shouldCenter={true} />;
  }
  if (shouldDelayTypesenseQuery) {
    return <Spinner centerToScreen={true} />;
  }

  const filter = createFilters({
    applicationId,
    charactersList,
    username,
    isPartner,
    shouldFilterNsfwCharacters,
    pagePath,
    foundNegativeKeyword: stateIsFoundNegativeKeyword,
    blockedUsers,
    defaultTag,
    collection,
    characterType,
  });

  const configProps = {
    hitsPerPage: 48,
    maxValuesPerFacet: 100,
    filters: filter,
  } as ConfigureProps;

  return (
    <InstantSearch
      searchClient={stateInstantSearchAdapter?.searchClient}
      indexName={searchIndexName}
      routing={routing}
      onStateChange={onSearchUiStateChange}
    >
      <FiltersV2Provider isEnabled={isFiltersV2}>
        <Configure {...configProps} />
        <Row className="flex-grow px-4 py-2.5 max-mob:p-2.5" data-testid="SearchClientCharacterListing">
          {!isHideFiltersBox ? (
            <FilterBox
              collection={collection}
              useStoreTagFilters={useStoreTagFilters}
              attribute="tags"
              operator="and"
              isTagsDrawerVisible={stateIsTagsDrawerVisible}
              setIsTagsDrawerVisible={setStateIsTagsDrawerVisible}
              isTagListMinimized={stateIsTagListMinimized}
            />
          ) : null}
          <Col className="gap-[18px]">
            {permissions?.includes("temporary_shadowban") ? <BannedBanner banType="shadow" /> : null}
            <HeroBanner />

            <div className="flex gap-2.5 justify-between max-mob:flex-col">
              <Row className="gap-2.5 grow">
                {!isHideFiltersBox && (
                  <SearchFilterTriggerSelector
                    isFiltersV2={isFiltersV2}
                    isTagsDrawerVisible={stateIsTagsDrawerVisible}
                    tagListMinimized={stateIsTagListMinimized}
                    setTagListMinimized={setStateIsTagListMinimized}
                    setTagsDrawer={setStateIsTagsDrawerVisible}
                    onlyDesktop
                  />
                )}
                <SearchBar
                  queryBy={stateQueryBy}
                  setQueryBy={setStateQueryBy}
                  defaultQueryBy={stateDefaultQueryBy}
                  isCreatorSearchEnabled={collection !== Collection.RULEBOOKS}
                  setFoundNegativeKeyword={setStateIsFoundNegativeKeyword}
                />
              </Row>
              <Row className="gap-2.5">
                {isNsfwCharacterDiscoveryAllowed && isNsfwUserToggleAllowed ? (
                  <NsfwToggle showNSFW={showNSFW} isNsfwToggled={stateIsNsfwToggled} setNsfwToggle={nsfwToggleHandler} />
                ) : null}
                <Row className="gap-2.5 max-mob:gap-[7px] max-mob:ml-auto">
                  {!isHideFiltersBox && (
                    <SearchFilterTriggerSelector
                      isFiltersV2={isFiltersV2}
                      isTagsDrawerVisible={stateIsTagsDrawerVisible}
                      tagListMinimized={stateIsTagListMinimized}
                      setTagListMinimized={setStateIsTagListMinimized}
                      setTagsDrawer={setStateIsTagsDrawerVisible}
                      notDesktop
                    />
                  )}
                  <SearchBySort
                    baseIndexName={baseIndexName}
                    currentRefinement={searchIndexName}
                    setCurrentCategory={setStateCurrentCategory}
                    collection={collection ?? Collection}
                  />
                </Row>
              </Row>
            </div>

            <WaitingQueueHeader />
            <Row className="items-center justify-between">
              {tabItems?.length && activeTab && onTabChange ? <Tabs tabItems={tabItems} activeTab={activeTab} onTabChange={onTabChange} /> : null}
              <SearchResultsSummary isFiltersV2={isFiltersV2} />
              {onRefresh ? (
                <Button
                  disabled={stateIsCharactersLoading}
                  variant={ButtonVariant.TRANSPARENT}
                  className={cn(stateIsCharactersLoading && "cursor-not-allowed")}
                  iconStart={IconVariants.REFRESH}
                  onClick={() => {
                    void onRefresh();
                  }}
                >
                  <Text variant={TextVariants.LABEL_MEDIUM_MEDIUM}>
                    <Trans i18nKey="chatbot:page.recommended.cta.refreshSuggestions" />
                  </Text>
                </Button>
              ) : null}
            </Row>
            <CharacterHits
              collection={collection}
              nsfwToggle={isEffectiveNsfwToggleEnabled}
              recommId={recommId}
              isPartner={isPartner}
              currentCategory={stateCurrentCategory}
              searchIndexName={searchIndexName}
              setIsCharactersLoading={setStateIsCharactersLoading}
              refetchingRecommendations={refetchingRecommendations}
              shouldFilterNsfwCharacters={shouldFilterNsfwCharacters}
            />
          </Col>
        </Row>
        <Spacer y={6} />
        <Row justify="center">
          <SearchClientPagination />
        </Row>
      </FiltersV2Provider>
    </InstantSearch>
  );
};

export default React.memo(SearchClientCharacterListing);
